//#region Libraries
import { useEffect, useState } from "react";
//#endregion
//#region Styles
import {
    Part, 
    TitleSection, 
    ParagraphSection, 
    Fields, Column, Row, SubtitleSection
} from "components/styles";
//#endregion
//#region Components
import ImageOption from "components/CustomRenderSelect/components/ImageOption/ImageOption";
import CustomRenderSelect from "components/CustomRenderSelect/CustomRenderSelect";
import NotificationNoContent from "components/NotificationNoContent/NotificationNoContent";
import SearchInDialogAlert from "components/SearchInDialogAlert/SearchInDialogAlert";
import DefinitionAcadReinforceType from "./components/DefinitionAcadReinforceType/DefinitionAcadReinforceType";
import Preloader from "components/Preloader/Preloader";
//#endregion
//#region Services
import { requestGetCoursesByCatalogCode } from "services/general";
//#endregion

const Part1 = ({
    acadReinforceTypes,
    changeField, 
    form, errors
}) => {
    //#region States
    const [showDialogSearchCourse, setShowDialogSearchCourse] = useState(false);
    const [matchedCourseList, setMatchedCourseList] = useState([]);
    const [mostSimilarCourse, setMostSimilarCourse] = useState("");
    const [loadingSearchCourse, setLoadingSearchCourse] = useState(false);
    const [timeoutIdToSearch, setTimeoutIdToSearch] = useState(null);
    //#endregion
    useEffect(() => {
        changeTitleField();
    }, [form.acadReinforceTypeId, form.course]);
    //#region Functions
    const changeTitleField = () => {
        const { acadReinforceTypeId, course } = form;
        if (!acadReinforceTypeId || !course.id) return;
        const { name: acadReinforceTypeName } = acadReinforceTypes.find(({ id }) => id === acadReinforceTypeId);
        changeField("title", `${acadReinforceTypeName} - ${course.name}`);
    }
    const toggleDialogSearchCourse = (value = null) => {
        setShowDialogSearchCourse(value !== null ? value : !showDialogSearchCourse);
    }
    const changeAcadReinforceTypeId = id => {
        changeField("acadReinforceTypeId", id, false);
    }
    const changeCourseField = ({ value: codes, text:name }) => {
        changeField("course", {
            ...codes,
            name
        }, false);
    }
    const fillMatchedCourseList = async courseCatalogCode => {
        // Borrando id del callback timeout si es que se teclea antes de la ejecución de la función
        clearTimeout(timeoutIdToSearch);
        // Programando ejecución de la búsqueda a 500ms
        const timeoutId = setTimeout(async () => {
            // Obteniendo referencia del elemento en focus
            const $input = document.activeElement;
            // Definiendo función de manejo del loader
            const handlerLoading = activateLoader => {
                if (!courseCatalogCode) 
                    return;
                $input[activateLoader ? "blur" : "focus"]();
                setLoadingSearchCourse(activateLoader);
            }
            handlerLoading(true);
            // Obteniendo lista de cursos
            const courseList = !courseCatalogCode ? [] : await requestGetCoursesByCatalogCode(courseCatalogCode);
            handlerLoading(false);
            // Llenando de lista de cursos
            setMatchedCourseList(courseList);
            // Verificando si no fue encontrado para buscar los similares
            if (courseCatalogCode && courseList.length === 0) {
                handlerLoading(true);
                const courseListMostSimilar = await requestGetCoursesByCatalogCode(courseCatalogCode[0]);
                handlerLoading(false);
                const newMostSimilar = courseListMostSimilar.find((
                    { text }) => {
                    return text[0].toUpperCase() === courseCatalogCode[0].toUpperCase();
                });
                setMostSimilarCourse(newMostSimilar?.text || "");
            }
        }, 500);
        setTimeoutIdToSearch(timeoutId);
    }
    const renderDefinitionByAcadReinforceType = () => {
        return acadReinforceTypes
            .filter(({ id }) => id === acadReinforceTypeId)
            .map(({ 
                name, 
                icon, description }) => {
                return (
                    <DefinitionAcadReinforceType
                        key={0}
                        icon={icon}
                        title={name}
                        description={description}/>
                )
            });
    }
    const clearMatchedCourseList = () => setMatchedCourseList([]);
    const { acadReinforceTypeId, course } = form;
    //#endregion
    return (
        <Part>
            <Fields>
                <Row>
                    <Column width="unset" gap="5px">
                        <TitleSection>Tipo de refuerzo</TitleSection>
                        <ParagraphSection>
                            Selecciona una opción
                        </ParagraphSection>
                        <CustomRenderSelect 
                            optionsProps={
                                acadReinforceTypes.map(item => {
                                    return {
                                        currentValue: item.id, 
                                        title: item.name, 
                                        src: item.icon
                                    };
                                })}
                            render={ImageOption}
                            value={acadReinforceTypeId}
                            onClick={changeAcadReinforceTypeId}
                            error={errors.acadReinforceTypeId}
                            errorText="Es necesario seleccionar un tipo de refuerzo académico"/>
                        <Column marginTop="20px">
                            {acadReinforceTypeId > 0 
                            && renderDefinitionByAcadReinforceType()}
                        </Column>
                    </Column>
                    <SearchInDialogAlert
                        inputButtonTextProps={{
                            placeholder: "Buscar un curso",
                            text: course.name,
                            error: errors.course,
                            errorText: "No has seleccionado un curso."
                        }}
                        placeholder="Escribe el código de catálogo del curso"
                        changeSearchField={changeCourseField}
                        data={matchedCourseList.map(({ id, catalogCode, text }) => {
                            return {
                                value: { 
                                    id, catalogCode
                                }, text
                            };
                        })}
                        minLength={7}
                        label="Curso:"
                        direction="row"
                        fillData={fillMatchedCourseList}
                        clearData={clearMatchedCourseList}
                        mostSimilar={mostSimilarCourse}
                        firstSelection={course.id}
                        name={course.name}
                        titleDialog="Busca el curso"
                        loadingSearch={loadingSearchCourse}
                        showDialogAlert={showDialogSearchCourse}
                        toggleDialogAlert={toggleDialogSearchCourse}
                        notFoundMessage="El curso que estás buscando no esta en la lista de cursos."/>
                </Row>
                {acadReinforceTypeId === 0 
                    && <Column>
                        <NotificationNoContent
                                title="Aún no has señalado un tipo de refuerzo"
                                description="Selecciona uno para mostrarte la definición"/>
                    </Column>}
            </Fields>
            <Preloader 
                hidden={!loadingSearchCourse}
                message="Buscando cursos..."/>
        </Part>
    );
}

export default Part1;